/**
 * Error handling utilities for API responses
 * Provides consistent error message parsing with meaningful fallbacks
 */

export interface ApiError {
  message: string
  status?: number
  code?: string
}

/**
 * Parses API error responses into consistent, user-friendly messages
 * Provides fallback messages when error parsing fails
 * 
 * @param error - The error object from axios or other sources
 * @param fallbackMessage - Default message to show if no error can be parsed
 * @returns ApiError object with message and optional status/code
 */
export function parseApiError(error: any, fallbackMessage: string = "Something went wrong"): ApiError {
  console.error('Parsing API error:', error)
  
  // Default result
  const result: ApiError = {
    message: fallbackMessage,
    status: error?.response?.status,
    code: error?.code
  }

  // If no error object, return fallback
  if (!error) {
    return result
  }

  // Network or timeout errors
  if (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR') {
    result.message = "Network error. Please check your connection and try again."
    return result
  }

  // No response (network error, timeout, etc.)
  if (!error.response) {
    if (error.message) {
      result.message = error.message
    }
    return result
  }

  const { data, status } = error.response

  // Handle specific status codes
  switch (status) {
    case 401:
      result.message = "Authentication required. Please log in again."
      return result
    case 403:
      result.message = "You don't have permission to perform this action."
      return result
    case 404:
      result.message = "The requested resource was not found."
      return result
    case 422:
      // Validation errors - try to extract meaningful messages
      break // Continue to data parsing below
    case 500:
      result.message = "Server error. Please try again later."
      return result
    case 503:
      result.message = "Service temporarily unavailable. Please try again later."
      return result
  }

  // Try to parse error data in order of preference
  if (data) {
    // 0. Check if data is HTML (error pages like 502 Bad Gateway)
    if (typeof data === 'string' && data.trim().toLowerCase().startsWith('<!doctype') || 
        (typeof data === 'string' && data.includes('<html'))) {
      console.warn('Received HTML error response instead of JSON:', data.substring(0, 200) + '...')
      result.message = `Server error (${status || 'unknown'}). Please try again later.`
      return result
    }

    // 1. Check for errors array (common Rails validation errors)
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      // Filter out empty/null values and join
      const validErrors = data.errors.filter((err: any) => 
        err && (typeof err === 'string' ? err.trim() !== '' : true)
      )
      if (validErrors.length > 0) {
        result.message = validErrors.length === 1 ? validErrors[0] : validErrors.join('. ')
        return result
      }
    }

    // 2. Check for single error message
    if (typeof data.error === 'string' && data.error.trim()) {
      result.message = data.error
      return result
    }

    // 3. Check for message field
    if (typeof data.message === 'string' && data.message.trim()) {
      result.message = data.message
      return result
    }

    // 4. Check for Rails-style field errors object
    if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
      const errorMessages = []
      for (const [field, messages] of Object.entries(data.errors)) {
        if (Array.isArray(messages) && messages.length > 0) {
          const fieldErrors = messages.filter(msg => msg && typeof msg === 'string' && msg.trim())
          if (fieldErrors.length > 0) {
            errorMessages.push(`${field}: ${fieldErrors.join(', ')}`)
          }
        } else if (typeof messages === 'string' && messages.trim()) {
          errorMessages.push(`${field}: ${messages}`)
        }
      }
      if (errorMessages.length > 0) {
        result.message = errorMessages.join('. ')
        return result
      }
    }

    // 5. If data is a string, check if it's HTML or plain text
    if (typeof data === 'string' && data.trim()) {
      const cleanData = data.trim()
      // If it's HTML content, provide a generic message
      if (cleanData.includes('<') && cleanData.includes('>')) {
        console.warn('Received HTML content in error response:', cleanData.substring(0, 100) + '...')
        result.message = `Server error (${status || 'unknown'}). Please try again later.`
      } else {
        result.message = cleanData
      }
      return result
    }
  }

  // If we still haven't found a message, use the error message if available
  if (error.message && typeof error.message === 'string' && error.message.trim()) {
    // Avoid showing technical error messages to users
    const technicalErrors = [
      'Network Error',
      'timeout',
      'Request failed',
      'Cannot read properties',
      'undefined'
    ]
    
    const isTechnical = technicalErrors.some(tech => 
      error.message.toLowerCase().includes(tech.toLowerCase())
    )
    
    if (!isTechnical) {
      result.message = error.message
      return result
    }
  }

  // Final fallback
  return result
}

/**
 * Common error messages for different actions
 */
export const ERROR_MESSAGES = {
  LOAD_FAILED: "Failed to load data",
  SAVE_FAILED: "Failed to save changes",
  DELETE_FAILED: "Failed to delete item",
  CREATE_FAILED: "Failed to create item", 
  UPDATE_FAILED: "Failed to update item",
  UPLOAD_FAILED: "Failed to upload file",
  NETWORK_ERROR: "Network error. Please check your connection.",
  PERMISSION_DENIED: "You don't have permission to perform this action",
  VALIDATION_ERROR: "Please check your input and try again",
  SERVER_ERROR: "Server error. Please try again later"
} as const

/**
 * Helper function to create error objects for toast notifications
 */
export function createErrorToast(error: any, fallbackMessage: string) {
  const parsedError = parseApiError(error, fallbackMessage)
  
  return {
    title: "Error",
    description: parsedError.message,
    variant: "destructive" as const,
  }
}

/**
 * Helper function to handle common API errors with toast notifications
 * Use this in components that have access to the toast function
 */
export function handleApiError(error: any, toast: any, fallbackMessage: string = "Something went wrong") {
  const errorToast = createErrorToast(error, fallbackMessage)
  toast(errorToast)
}
